/**
 * Sentry Configuration for Multi-Environment Setup
 * 
 * This file handles different Sentry DSNs based on environment and service type.
 * It supports:
 * - prod-web (Production Frontend)
 * - dev-web (Development Frontend)
 * - prod-backend (Production Edge Functions)
 * - dev-backend (Development Edge Functions)
 */

export interface SentryConfig {
  dsn: string;
  environment: string;
  serviceType: 'web' | 'backend';
}

/**
 * Get Sentry configuration based on current environment
 */
export function getSentryConfig(): SentryConfig {
  const isProduction = import.meta.env.PROD;
  const environment = isProduction ? 'production' : 'development';
  const serviceType = 'web'; // This is the frontend configuration

  // Get the web DSN (optional - app will still work without Sentry)
  const dsn = import.meta.env.VITE_SENTRY_DSN_WEB;

  if (!dsn) {
    console.warn(`VITE_SENTRY_DSN_WEB environment variable is missing. Sentry will be disabled for ${environment}-${serviceType}.`);
    // Return config with empty DSN - Sentry won't initialize but app won't break
    return {
      dsn: '',
      environment,
      serviceType,
    };
  }

  console.log(`Sentry configuration: ${environment}-${serviceType}`);

  return {
    dsn,
    environment,
    serviceType,
  };
}

/**
 * Get Sentry configuration for a specific service type
 */
export function getSentryConfigFor(serviceType: 'web' | 'backend'): SentryConfig {
  const isProduction = import.meta.env.PROD;
  const environment = isProduction ? 'production' : 'development';
  
  const envKey = serviceType === 'web' ? 'VITE_SENTRY_DSN_WEB' : 'VITE_SENTRY_DSN_BACKEND';
  const dsn = import.meta.env[envKey];

  if (!dsn) {
    console.warn(`${envKey} environment variable is missing. Sentry will be disabled for ${environment}-${serviceType}.`);
    return {
      dsn: '',
      environment,
      serviceType,
    };
  }

  return {
    dsn,
    environment,
    serviceType,
  };
}
